(() => {
  const tag = 'nw-deferred-payment-terms';
  if (customElements.get(tag)) return;

  const hosts = new Set();
  let intentReceived = false;
  const intentEvents = ['pointerdown', 'keydown', 'wheel', 'touchmove'];

  function receiveIntent() {
    if (intentReceived) return;
    intentReceived = true;
    intentEvents.forEach(name => window.removeEventListener(name, receiveIntent, true));
    hosts.forEach(host => host.queueLoad());
  }

  intentEvents.forEach(name => window.addEventListener(name, receiveIntent, { capture: true, passive: true }));

  class DeferredPaymentTerms extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.form = this.closest('form');
      this.trigger = this.querySelector('[data-payment-information-trigger]');
      this.status = this.querySelector('[data-payment-information-status]');
      this.content = this.querySelector('[data-payment-information-content]');
      if (!this.form || !this.trigger || !this.status || !this.content) return;
      this.connected = true;
      this.requestId = this.requestId || 0;
      this.label = this.label || this.trigger.textContent;
      this.onClick = () => {
        if (this.trigger.getAttribute('aria-disabled') === 'true') return;
        receiveIntent();
        this.queueLoad(true);
      };
      this.onChange = () => {
        if (intentReceived) this.queueLoad(false, 40);
      };
      this.trigger.addEventListener('click', this.onClick);
      this.form.addEventListener('change', this.onChange, true);
      this.form.addEventListener('input', this.onChange, true);
      hosts.add(this);
      this.hidden = false;
      this.style.display = 'block';
      if (intentReceived) this.queueLoad();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.requestId = (this.requestId || 0) + 1;
      this.controller?.abort();
      this.controller = null;
      this.pendingKey = null;
      this.removeAttribute('aria-busy');
      this.trigger?.removeAttribute('aria-disabled');
      this.form?.removeEventListener('change', this.onChange, true);
      this.form?.removeEventListener('input', this.onChange, true);
      this.trigger?.removeEventListener('click', this.onClick);
      hosts.delete(this);
      this.connected = false;
    }

    readState() {
      const fields = new FormData(this.form);
      const variant = String(fields.get('id') || '');
      if (!/^\d+$/.test(variant)) return null;
      const quantity = String(Math.max(1, Number.parseInt(fields.get('quantity'), 10) || 1));
      const sellingPlan = String(fields.get('selling_plan') || '');
      const url = new URL(this.dataset.productUrl, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      url.searchParams.set('section_id', 'nw-deferred-payment-terms');
      url.searchParams.set('variant', variant);
      url.searchParams.set('quantity', quantity);
      if (sellingPlan) url.searchParams.set('selling_plan', sellingPlan);
      else url.searchParams.delete('selling_plan');
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      return { variant, quantity, sellingPlan, url, key: url.href };
    }

    queueLoad(force = false, delay = 0) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.load(force), delay);
    }

    async load(force = false) {
      if (!this.isConnected || !this.connected) return;
      const state = this.readState();
      if (!state || this.pendingKey === state.key || (!force && this.loadedKey === state.key)) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const requestId = ++this.requestId;
      this.pendingKey = state.key;
      if (this.loadedKey && this.loadedKey !== state.key) {
        this.content.replaceChildren();
        this.loadedKey = null;
      }
      this.setAttribute('aria-busy', 'true');
      this.trigger.hidden = false;
      this.trigger.setAttribute('aria-disabled', 'true');
      this.trigger.textContent = this.dataset.loadingLabel;
      this.status.classList.add('visually-hidden');
      this.status.textContent = this.dataset.loadingLabel;
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 15000);
      try {
        const response = await fetch(state.url.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Payment information response unavailable');
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const terms = documentFragment.querySelector('shopify-payment-terms');
        if (!terms || terms.getAttribute('variant-id') !== state.variant) {
          throw new Error('Payment information response does not match selected variant');
        }
        if (!this.isConnected || requestId !== this.requestId) return;
        if (this.readState()?.key !== state.key) {
          this.queueLoad();
          return;
        }
        this.content.replaceChildren(document.importNode(terms, true));
        this.loadedKey = state.key;
        this.status.textContent = '';
        this.trigger.textContent = this.label;
        this.trigger.removeAttribute('aria-disabled');
        if (document.activeElement === this.trigger) {
          this.trigger.addEventListener('blur', () => {
            if (this.loadedKey) this.trigger.hidden = true;
          }, { once: true });
        } else {
          this.trigger.hidden = true;
        }
      } catch (error) {
        if (!this.isConnected || requestId !== this.requestId || (controller.signal.aborted && !timedOut)) return;
        this.trigger.hidden = false;
        this.trigger.removeAttribute('aria-disabled');
        this.trigger.textContent = this.label;
        this.status.textContent = this.dataset.errorLabel;
        this.status.classList.remove('visually-hidden');
      } finally {
        clearTimeout(timeout);
        if (requestId === this.requestId) {
          this.pendingKey = null;
          this.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define(tag, DeferredPaymentTerms);
})();



